// Constants
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
// Styles
import { concatStyleSets, FontSizes, memoizeFunction } from "@fluentui/react";
import { highContrastColor } from "../../../utilities/HighContrast.styles";
import { flexChildSizeFlexible, fontColorNeutralSecondary, styleButtonAsDiv } from "../../../utilities/Mixin.styles";
import { clickableBorderAndColors } from "../legacyPlanner/taskCard/TaskCard.styles";
// Utilities
import { getPriorityColor, getProgressColor } from "../../../utilities/ColorUtilities";
/**
 * Helper to get base styles for indicators
 * @param theme Planner theme
 */ export const getBaseIndicatorStyles = memoizeFunction((theme)=>{
    const root = [
        flexChildSizeFlexible("auto"),
        styleButtonAsDiv,
        fontColorNeutralSecondary(theme),
        {
            fontSize: FontSizes.xSmall,
            height: "100%"
        }
    ];
    const rootHovered = [
        // Fix for IE changing color on hover (high contrast mode)
        fontColorNeutralSecondary(theme),
        {
            "&:hover": {
                backgroundColor: theme.semanticColors.buttonBackgroundHovered,
                ".ms-Button-icon, .ms-Icon, svg, .ms-Button-label": {
                    color: theme.semanticColors.buttonTextHovered
                }
            }
        }
    ];
    const flexContainer = {
        padding: "0",
        margin: "0"
    };
    const icon = [
        highContrastColor(),
        fontColorNeutralSecondary(theme),
        {
            padding: "0",
            margin: "2px",
            height: "100%"
        }
    ];
    const iconHovered = icon;
    const iconPressed = icon;
    const textContainer = {
        letterSpacing: "0.75px",
        padding: "0 0 1px 0",
        margin: "0"
    };
    const label = [
        fontColorNeutralSecondary(theme),
        {
            fontSize: FontSizes.xSmall,
            padding: "0",
            margin: "2px"
        }
    ];
    return {
        root,
        rootHovered,
        flexContainer,
        icon,
        iconHovered,
        iconPressed,
        textContainer,
        label
    };
});
/**
 * Helper to get styles for PriorityIndicator
 * @param theme Planner theme
 * @param priorityName Task priority
 * @param isClickable Set true to apply clickable styles
 */ export const getPriorityIndicatorStyles = memoizeFunction((theme, priorityName, isClickable)=>{
    const root = isClickable && clickableBorderAndColors(theme);
    const rootExpanded = theme.name === PlannerTheme.HighContrast && {
        color: Colors.White
    };
    // Even though the indicator IconProps already include the color styling,
    // IButtonStyles has hovered and pressed styles that do not automatically apply the IconProps styles.
    // So, we must pass in the color here again to cover hovered and pressed cases.
    const iconActiveColor = theme.name === PlannerTheme.HighContrast ? Colors.Black : getPriorityColor(theme, priorityName);
    const iconHovered = {
        color: iconActiveColor
    };
    const iconPressed = iconHovered;
    const iconExpandedHovered = iconHovered;
    const priorityIndicatorStyles = {
        root,
        rootExpanded,
        iconHovered,
        iconPressed,
        iconExpandedHovered
    };
    return concatStyleSets(getBaseIndicatorStyles(theme), priorityIndicatorStyles);
});
/**
 * Helper to get styles for ProgressIndicator
 * @param theme Planner theme
 * @param progressState Task progress
 * @param isClickable Set true to apply clickable styles
 */ export const getProgressIndicatorStyles = memoizeFunction((theme, progressState, isClickable)=>{
    const root = isClickable && clickableBorderAndColors(theme);
    const rootExpanded = theme.name === PlannerTheme.HighContrast && {
        color: Colors.White
    };
    // Even though the indicator IconProps already include the color styling,
    // IButtonStyles has hovered and pressed styles that do not automatically apply the IconProps styles.
    // So, we must pass in the color here again to cover hovered and pressed cases.
    const iconActiveColor = theme.name === PlannerTheme.HighContrast ? Colors.Black : getProgressColor(theme, progressState);
    const iconHovered = {
        color: iconActiveColor
    };
    const iconPressed = iconHovered;
    const iconExpandedHovered = iconHovered;
    const priorityIndicatorStyles = {
        root,
        rootExpanded,
        iconHovered,
        iconPressed,
        iconExpandedHovered
    };
    return concatStyleSets(getBaseIndicatorStyles(theme), priorityIndicatorStyles);
});
